import { getUserToken } from "../helpers/authHelpers";
import { API_SERVER_URL } from '../config/api';
import { reportApiFailure } from "./errorReporter";
import { mysteryTeamHeaders } from "../helpers/mysteryTeamHeader";

interface FetchParams {
  method: HttpMethod;
  endpoint: string;
  payload?: unknown;
  /**
   * Skip the global `window.handleApiError` toast on failure. The promise still rejects with
   * the same ApiError — for a caller that alerts its own, more specific wording instead.
   */
  silent?: boolean;
}

export interface ApiError extends Error {
  status: number;
  statusText: string;
  endpoint: string;
  method: string;
  body: string;
  detailedMessage?: string;
  data?: any;
}

const buildApiError = (
  status: number,
  statusText: string,
  rawBody: string,
  endpoint: string,
  method: string
): ApiError => {
  let parsed: any = undefined;
  let detailedMessage: string | undefined;
  if (rawBody) {
    try {
      parsed = JSON.parse(rawBody);
      detailedMessage =
        parsed?.detailedMessage ||
        parsed?.message ||
        parsed?.error ||
        (Array.isArray(parsed?.errors) ? parsed.errors.join("; ") : undefined);
    } catch {
      // body wasn't JSON — leave as raw text
    }
  }
  const human = detailedMessage || rawBody || statusText || `HTTP ${status}`;
  const err = new Error(`[${status}] ${human}`) as ApiError;
  err.name = "ApiError";
  err.status = status;
  err.statusText = statusText;
  err.endpoint = endpoint;
  err.method = method;
  err.body = rawBody;
  err.detailedMessage = detailedMessage;
  err.data = parsed;
  return err;
};

/**
 * Fetch rejections seen by fetchWithToken, so the outer frame can tell a network failure on the
 * 401 retry from any other error (a token refresh failing, say) without touching the error.
 */
const networkFailures = new WeakSet<object>();

/** The System Health reporter must never change what the caller sees, so even its call is guarded. */
const safeReport = (failure: Parameters<typeof reportApiFailure>[0]) => {
  try {
    reportApiFailure(failure);
  } catch {
    // the reporter is passive
  }
};

const reportFailure = (err: unknown, url: string) => {
  if (typeof err !== "object" || err === null) return;
  if (networkFailures.has(err)) {
    // An aborted fetch is the user cancelling (an upload dialog closed), not a network failure.
    if ((err as Error).name === "AbortError") return;
    safeReport({ apiPath: url, kind: "NETWORK", message: (err as Error).message || "Network failure" });
    return;
  }
  const apiError = err as Partial<ApiError>;
  if (apiError.name === "ApiError" && typeof apiError.status === "number" && apiError.status >= 500) {
    safeReport({
      apiPath: url,
      kind: "API_5XX",
      httpStatus: apiError.status,
      message: `HTTP ${apiError.status}${apiError.statusText ? ` ${apiError.statusText}` : ""}`,
    });
  }
};

/**
 * A 5xx answer or a rejected fetch is handed to the System Health error reporter (4xx never are),
 * and the same error is then thrown on unchanged. Only the outer frame (`isRetry === false`)
 * reports, because it sees the final outcome: a 401 → retry → 503 is reported once, not twice.
 */
export const fetchWithToken = async <T>(
  params: FetchParams,
  isRetry = false
): Promise<T> => {
  if (isRetry) return fetchOnce<T>(params, true);
  try {
    return await fetchOnce<T>(params, false);
  } catch (err) {
    reportFailure(err, params.endpoint);
    throw err;
  }
};

const fetchOnce = async <T>(
  params: FetchParams,
  isRetry: boolean
): Promise<T> => {
  let currentToken = await getUserToken(isRetry);
  const { method, endpoint, payload, silent } = params;
  let response: Response;
  try {
    response = await fetch(endpoint, {
      method,
      body: payload ? JSON.stringify(payload) : null,
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${currentToken}`,
        ...mysteryTeamHeaders(endpoint),
      }),
    });
  } catch (err) {
    if (typeof err === "object" && err !== null) networkFailures.add(err);
    throw err;
  }

  if (!response.ok && response.status !== 201 && response.status !== 204) {
    if (response.status === 401 && !isRetry) {
      return fetchWithToken({ method, endpoint, payload, silent }, true);
    }
    const rawBody = await response.text();
    const err = buildApiError(response.status, response.statusText, rawBody, endpoint, method);
    if (!silent && typeof window !== 'undefined' && window.handleApiError) {
      window.handleApiError(err.message);
    }
    return Promise.reject(err);
  }

  // 204 No Content (and 200 OK on endpoints that return void) come back with an
  // empty body. Calling response.json() on those throws SyntaxError, which surfaces
  // as a fake "failed to delete" error in the UI. Read as text first and only
  // parse when we actually have something.
  if (response.status === 204) {
    return {} as T;
  }
  const text = await response.text();
  return (text ? JSON.parse(text) : {}) as T;
};

export const fetchCSVWithToken = async (
  params: FetchParams,
  isRetry = false
): Promise<any> => {
  let currentToken = await getUserToken(isRetry);
  const { method, endpoint, payload } = params;
  const response = await fetch(endpoint, {
    method,
    body: payload ? JSON.stringify(payload) : null,
    headers: new Headers({
      "Content-Type": "application/json",
      Authorization: `Bearer ${currentToken}`,
      ...mysteryTeamHeaders(endpoint),
    }),
  });

  if (!response.ok && response.status !== 201) {
    if (response.status === 401 && !isRetry) {
      return fetchWithToken({ method, endpoint, payload }, true);
    }
    const errorMessage = await response.text();
    throw new Error(errorMessage);
  }
  return response.text();
};

interface UploadFileParams {
  endpoint: string;
  payload?: File;
  // Extra multipart form fields sent alongside the file (e.g. autoCreateSet=true).
  fields?: Record<string, string>;
}
export const uploadFileWithToken = async <T>(
  params: UploadFileParams,
  isRetry = false
): Promise<T> => {
  let currentToken = await getUserToken(isRetry);
  const { endpoint, payload, fields } = params;
  const form = new FormData();
  if (payload) {
    form.append("file", payload);
  }
  if (fields) {
    Object.entries(fields).forEach(([key, value]) => form.append(key, value));
  }

  const response = await fetch(endpoint, {
    method: "POST",
    body: form,
    headers: new Headers({
      Authorization: `Bearer ${currentToken}`,
      ...mysteryTeamHeaders(endpoint),
    }),
  });

  if (!response.ok && response.status !== 201) {
    if (response.status === 401 && !isRetry) {
      return uploadFileWithToken({ endpoint, payload, fields }, true);
    }
    const rawBody = await response.text();
    const err = buildApiError(response.status, response.statusText, rawBody, endpoint, "POST");
    if (typeof window !== 'undefined' && window.handleApiError) {
      window.handleApiError(err.message);
    }
    throw err;
  }
  return response.json();
};

export const setupWebhooks = async (region: string) => {
  const response = await fetchWithToken<any>({
    method: "POST",
    endpoint: `${API_SERVER_URL}/webhook/setup/${region}`,
  });
  return response;
};

export const fetchWebhooks = async (region: string) => {
  const response = await fetchWithToken<any[]>({
    method: "GET",
    endpoint: `${API_SERVER_URL}/webhook/list/${region}`,
  });
  return response;
};